import { flowRight, isEmpty, get, omit } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import { connect } from '../../../common/components/runtime-context';
import { getCommentsPerPage, POSTS_PER_PAGE } from '../../constants/pagination';
import AppLoaded from '../../components/app-loaded';
import AccountSuspended from '../../components/account-suspended';
import AnimatedLoader from '../../components/animated-loader';
import Button from '../../components/button';
import CommentingDisabled from '../../components/commenting-disabled';
import EmptyComment from '../../components/empty-comment';
import EmptyStates from '../../components/empty-states';
import Link from '../../components/link/internal-link';
import PostPageHeaderActions from '../../components/post-page-header-actions';
import PageMetadata from '../../components/page-metadata';
import Pagination from '../../components/pagination-comments';
import PostContent from '../../components/post-content';
import PostHeader from '../../components/post-header';
import PostPageLayout from '../../components/post-page-layout';
import PostPageStats from '../../components/post-page-stats';
import PostTitle from '../../components/post-title';
import TimeAgo from '../../components/time-ago';
import LoadMoreMobilePagination from '../../components/load-more-mobile-pagination';
import { HorizontalSeparator } from '../../components/separator';
import PostCommentList from '../post-comment-list';
import PostMainActions from '../post-main-actions';
import PostPageFooter from '../post-page-footer';
import FooterPosts from '../../components/footer-posts';
import contentStateToString from '../../services/content-state-to-string';
import { getPostCover } from '@wix/communities-universal/dist/src/services/get-post-cover';
import { getLastPage } from '../../services/pagination';
import { getCategoryBySlug } from '../../selectors/categories-selectors';
import { getCommentsByPostId } from '../../selectors/comment-selectors';
import {
  getVideoThumbnailMetadata,
  getImageMetadataFromWixMedia,
} from '../../components/page-metadata/image-adapter';
import { getCurrentUserId } from '../../../common/store/current-user/current-user-selectors';
import { getIsPostLoaded, getIsCommentsLoaded } from '../../selectors/is-loaded-selectors';
import { getIsPostLoading, getIsCommentsLoading } from '../../selectors/is-loading-selectors';
import { getEntityCount } from '../../selectors/pagination-selectors';
import { getPostBySlug } from '../../selectors/post-selectors';
import { getIsDemoMode } from '../../../common/store/instance-values/instance-values-selectors';
import withAuth from '../../hoc/with-auth';
import withCardBorderWidth from '../../hoc/with-card-border-width';
import withDeviceType from '../../hoc/with-device-type';
import withFontClassName from '../../hoc/with-font-class-name';
import withTranslate from '../../hoc/with-translate';
import styles from './post-page.scss';
import { getRouteParams, getPreviousMatches } from '../../../common/router/router-selectors';
import { getLocation, getSectionUrl } from '../../../common/store/location/location-selectors';
import { scrollToComment } from '../../services/scroll-to-comment';
import { getDeepLinkParams } from '../../services/get-deep-link-params';
import { POST_PAGE } from '../../constants/page-names';
import {
  ROUTE_CREATE_POST,
  ROUTE_CATEGORY_CREATE_POST,
  ROUTE_POST_EDIT,
} from '../../constants/routes';

export class PostPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      initialPage: props.page,
    };
  }

  postOpened() {
    const { isDemoMode, post, emitOpenPost, incrementPostViewCount, pageOpened } = this.props;

    if (!isDemoMode && post._id) {
      emitOpenPost(post);
      incrementPostViewCount(post._id);
      pageOpened({
        page: POST_PAGE,
        post,
      });
    }
  }

  componentDidMount = () => {
    const { commentToFocusId } = this.props;
    if (commentToFocusId) {
      scrollToComment(commentToFocusId);
    }
    this.postOpened();
  };

  componentDidUpdate(prevProps) {
    if (prevProps.post._id !== this.props.post._id) {
      this.postOpened();
    }

    if (prevProps.commentToFocusId !== this.props.commentToFocusId) {
      scrollToComment(this.props.commentToFocusId);
    }
  }

  componentWillUnmount = () => {
    this.props.setIsLoaded('comments', this.props.params.postSlug, false);
    if (!isEmpty(this.props.category)) {
      this.props.emitClosePost(this.props.post);
    }
    this.props.clearComments();
  };

  handleEmptyCommentMouseDown = () => {
    if (document.activeElement.tagName.toLowerCase() === 'textarea') {
      this.ignoreClick = true;
    }
  };

  handleEmptyCommentClick = () => {
    if (!this.ignoreClick) {
      this.props.focusCommentForm();
    }
    delete this.ignoreClick;
  };

  renderEmptyComment = () => {
    if (!this.props.allComments.length && !this.props.post.isCommentsDisabled) {
      return (
        <EmptyComment
          onClick={this.handleEmptyCommentClick}
          onMouseDown={this.handleEmptyCommentMouseDown}
        />
      );
    }
  };

  renderPaginationTop = () => {
    const { page, entityCount, isMobile, post } = this.props;

    if (!isMobile) {
      return (
        <Pagination
          page={page}
          entityCount={entityCount}
          showPerPage={getCommentsPerPage(isMobile)}
          onChange={this.handlePageChange}
          buildPageUrl={this.buildPageUrl}
          rightSideRender={() => <PostPageHeaderActions post={post} />}
          isTop
        />
      );
    }
  };

  renderPagination = () => {
    const { page, entityCount, isMobile, isCommentsLoaded, isCommentsLoading, t } = this.props;
    const commentsPerPage = getCommentsPerPage(isMobile);
    if (isCommentsLoaded && entityCount > commentsPerPage) {
      if (isMobile) {
        return (
          <LoadMoreMobilePagination
            loadMore={this.loadMoreMobile}
            isLoading={isCommentsLoading}
            hasMore={this.props.page < Math.max(Math.ceil(entityCount / commentsPerPage))}
            loadActionText={t('post.load-comments')}
          />
        );
      } else {
        return (
          <Pagination
            page={page}
            entityCount={entityCount}
            showPerPage={commentsPerPage}
            onChange={this.handlePageChange}
            buildPageUrl={this.buildPageUrl}
          />
        );
      }
    }
  };

  buildPageUrl = (page = 1) => {
    const {
      params: { postSlug, categorySlug },
    } = this.props;
    const isFirstPage = page === 1;
    return isFirstPage ? `/${categorySlug}/${postSlug}` : `/${categorySlug}/${postSlug}/p-${page}`;
  };

  handlePageChange = ({ page }) => {
    const {
      post,
      params: { postSlug },
    } = this.props;
    this.props.fetchCommentsWithReplies(post, postSlug, page);
    this.props.navigateWithinForum(this.buildPageUrl(page));
  };

  loadMoreMobile = () => {
    const {
      post,
      params: { postSlug },
      page,
    } = this.props;
    this.props.fetchCommentsWithReplies(post, postSlug, page + 1);
    this.props.navigateWithinForum(this.buildPageUrl(page + 1));
  };

  renderEditDate = () => {
    const { post, t } = this.props;

    if (post.editedDate) {
      return (
        <p className={styles.edited}>
          {t('post-page.edited')}&nbsp;
          <span className={styles.editedDate}>
            <TimeAgo time={post.editedDate} />
          </span>
        </p>
      );
    }
  };

  renderFooter = () => {
    const { isBlocked, post, t, allComments, isCommentsLoading, isCommentsLoaded } = this.props;
    if (isBlocked || post.isCommentsDisabled) {
      return (
        <CommentingDisabled
          message={t(isBlocked ? 'post-page.commenting-blocked' : 'post-page.commenting-disabled')}
        />
      );
    }

    const showFooter = (!isCommentsLoading && isCommentsLoaded) || !isEmpty(allComments);
    if (!showFooter) {
      return null;
    }

    return (
      <div className={styles.footerContainer}>
        <PostPageFooter postId={post._id} />
      </div>
    );
  };

  renderPost = () => {
    const {
      category,
      post,
      isAnimated,
      borderWidth,
      titleFontClassName,
      page,
      isMobile,
      allComments,
      t,
    } = this.props;
    const hasComments = !isEmpty(allComments);

    const initiallyStartedOnFirstPAge = this.state.initialPage === 1;
    if (page > 1 && isMobile /*&& !initiallyStartedOnFirstPAge*/) {
      // TODO after scroll issue is fixed
      return (
        <div className="forum-card-background-color">
          <HorizontalSeparator className={styles.linkToPostSeparator} />
          <Link
            className={classNames(styles.linkToPost, 'forum-text-color')}
            to={`/${category.slug}/${post.slug}`}
            data-hook="back-to-post"
          >
            {t('post-page.back-to-discussion')}
          </Link>
          <HorizontalSeparator className={styles.linkToPostSeparator} />
        </div>
      );
    }

    if (!isMobile && page > 1) {
      return null;
    }

    const postContainerClassName = classNames(
      styles.postContainer,
      { [styles.withComments]: hasComments },
      'forum-card-background-color',
      'forum-card-border-color',
      'forum-text-color',
    );

    return (
      <React.Fragment>
        {isMobile && <HorizontalSeparator />}
        <main className={postContainerClassName} style={{ borderWidth }}>
          <div className={styles.postContentWrapper} data-hook="post-page__post">
            <PostHeader
              type={PostHeader.SINGLE}
              post={post}
              isAnimated={isAnimated}
              showDateBelowAvatar={isMobile}
            />
            <PostTitle
              className={titleFontClassName}
              type={PostTitle.SINGLE}
              title={post.title || ''}
              component="h1"
            />
            <div className={styles.postStatsContainer}>
              <PostPageStats
                viewCount={post.viewCount}
                commentCount={post.totalComments}
                isAnimated={isAnimated}
              />
              {this.renderEditDate()}
            </div>
            <div className={styles.postContent}>
              <PostContent post={post} />
            </div>
            <PostMainActions category={category} post={post} />
          </div>
        </main>
        {!isMobile && hasComments && (
          <div
            className={classNames(
              styles.postCommentsSeparatorContainer,
              'forum-card-background-color',
              'forum-card-border-color',
            )}
            style={{ borderWidth }}
          >
            <HorizontalSeparator className={styles.postCommentsSeparator} />
          </div>
        )}
      </React.Fragment>
    );
  };

  renderEmpty = () => {
    const { t } = this.props;
    return (
      <EmptyStates
        className={styles.emptyState}
        title={t('post-page.not-found-title')}
        content={t('post-page.not-fount-content')}
      >
        <Button component={Link} to="/">
          {t('post-page.back-to-forum')}
        </Button>
      </EmptyStates>
    );
  };

  buildPagingData = () => {
    const { page, entityCount } = this.props;
    const lastPage = getLastPage(entityCount, POSTS_PER_PAGE);
    const prev = page > 1 ? page - 1 : undefined;
    const next = page < lastPage ? page + 1 : undefined;
    return {
      current: page,
      next,
      prev,
      total: lastPage,
    };
  };

  render = () => {
    const {
      params: { postSlug },
      isBlocked,
      category,
      page,
      post,
      contentFontClassName,
      isLoading,
      isLoaded,
      isCommentsLoading,
      isCommentsLoaded,
      allComments,
      borderWidth,
      isMobile,
      commentToFocusId,
    } = this.props;

    if (isEmpty(post) && !isLoading) {
      return this.renderEmpty();
    }

    if (!isEmpty(category) && category.isPrivate && isBlocked) {
      return <AccountSuspended className={styles.emptyState} />;
    }

    const showCommentsLoader = isCommentsLoading && !isCommentsLoaded && isEmpty(allComments);
    const postCover = getPostCover(post);
    const entity = omit(post, 'content');
    const refersTo = `${category.slug}/${post.slug}`;
    return (
      <PostPageLayout className={classNames(styles.postPage, contentFontClassName)}>
        <PageMetadata
          title={post.title}
          description={contentStateToString(post.content)}
          image={
            postCover.shouldRender
              ? postCover.videoMetadata
                ? null
                : getImageMetadataFromWixMedia(postCover.imageMetadata)
              : null
          }
          video={
            postCover.shouldRender
              ? postCover.videoMetadata
                ? getVideoThumbnailMetadata(postCover.videoMetadata)
                : null
              : null
          }
          entity={entity}
          entityType="post"
          page={page}
          refersTo={refersTo}
          paging={this.buildPagingData()}
        />
        <AnimatedLoader isLoading={isLoading && !isLoaded && isEmpty(post)}>
          {this.renderPaginationTop()}
          {this.renderPost()}
          <AnimatedLoader isLoading={showCommentsLoader} isAnimated>
            {isEmpty(allComments) && !isMobile ? null : (
              <div
                className={classNames(
                  styles.commentsContainer,
                  'forum-card-background-color',
                  'forum-card-border-color',
                )}
                style={{ borderWidth }}
              >
                {this.renderEmptyComment()}
                <PostCommentList
                  isDeepLinked={commentToFocusId}
                  page={this.props.page}
                  postSlug={postSlug}
                />
                {this.renderPagination()}
              </div>
            )}
            {this.renderFooter()}
          </AnimatedLoader>
        </AnimatedLoader>
        <FooterPosts className={styles.footerPosts} />
        <AppLoaded />
      </PostPageLayout>
    );
  };
}

PostPage.propTypes = {
  fetchRepliesToPagePromisified: PropTypes.func.isRequired,
  incrementPostViewCount: PropTypes.func.isRequired,
  incrementCommentLikeCount: PropTypes.func.isRequired,
  category: PropTypes.object.isRequired,
  post: PropTypes.object.isRequired,
  allComments: PropTypes.array,
  focusCommentForm: PropTypes.func.isRequired,
  location: PropTypes.object,
  page: PropTypes.number,
  commentToFocusId: PropTypes.string,
  isMobile: PropTypes.bool,
  emitClosePost: PropTypes.func.isRequired,
  pageOpened: PropTypes.func.isRequired,
  t: PropTypes.func,
  typing: PropTypes.array,
  ownerId: PropTypes.string,
  entityCount: PropTypes.number,
  isAuthenticated: PropTypes.bool,
  isBlocked: PropTypes.bool,
  params: PropTypes.object,
  isAnimated: PropTypes.object,
  borderWidth: PropTypes.number.isRequired,
  titleFontClassName: PropTypes.string.isRequired,
  contentFontClassName: PropTypes.string.isRequired,
  setIsLoaded: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoaded: PropTypes.bool.isRequired,
  isCommentsLoading: PropTypes.bool.isRequired,
  isCommentsLoaded: PropTypes.bool.isRequired,
  navigateWithinForum: PropTypes.func.isRequired,
  fetchCommentsWithReplies: PropTypes.func.isRequired,
  clearComments: PropTypes.func.isRequired,
  sectionUrl: PropTypes.string,
};

const mapRuntimeToProps = (state, ownProps, actions) => {
  const params = getRouteParams(state);
  const post = getPostBySlug(state, params.postSlug) || {};
  const page = parseInt(params.page, 10) || 1;
  const { commentToFocusId } = getDeepLinkParams(params.deepLinkData);
  const prevRoute = get(getPreviousMatches(state)[1], 'route'); // take 1st member, since 0 is the current route

  return {
    isAnimated: [ROUTE_CREATE_POST, ROUTE_CATEGORY_CREATE_POST, ROUTE_POST_EDIT].includes(
      prevRoute,
    ),
    sectionUrl: getSectionUrl(state),
    category: getCategoryBySlug(state, params.categorySlug) || {},
    post,
    allComments: getCommentsByPostId(state, post._id),
    typing: state.typing,
    ownerId: getCurrentUserId(state),
    isLoading: getIsPostLoading(state, params.postSlug),
    isLoaded: getIsPostLoaded(state, params.postSlug),
    isCommentsLoading: getIsCommentsLoading(state, params.postSlug),
    isCommentsLoaded: getIsCommentsLoaded(state, params.postSlug),
    isDemoMode: getIsDemoMode(state),
    entityCount: getEntityCount(state, 'comments', post._id),
    page,
    commentToFocusId,
    fetchCommentsWithReplies: actions.fetchCommentsWithReplies,
    // fetchInitialReplies: actions.fetchInitialReplies,
    fetchRepliesToPagePromisified: actions.fetchRepliesToPagePromisified,
    incrementPostViewCount: actions.incrementPostViewCount,
    incrementCommentLikeCount: actions.incrementCommentLikeCount,
    focusCommentForm: () => console.log('focous', 'comment', 'content'),
    emitClosePost: actions.emitClosePost,
    pageOpened: actions.pageOpened,
    setIsLoaded: actions.setIsLoaded,
    navigateWithinForum: actions.navigateWithinForum,
    clearComments: actions.clearComments,
    emitOpenPost: actions.emitOpenPost,
    location: getLocation(state),
    params,
  };
};

export default flowRight(
  connect(mapRuntimeToProps),
  withDeviceType,
  withFontClassName,
  withCardBorderWidth,
  withAuth,
  withTranslate,
)(PostPage);
